import { dropTask } from 'ember-concurrency';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import { PhTrayArrowDown } from 'ember-phosphor-icons';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type FeaturesService from '../../services/features.ts';

export interface EligibilityFilesDownloadEligibilityFileSignature {
  Args: {
    legacyId: string;
    companyId: string;
    processId: string;
    fileName: string;
    title: string;
    format: string;
  };
  Element: HTMLElement;
}

export default class EligibilityFilesDownloadEligibilityFileComponent extends Component<EligibilityFilesDownloadEligibilityFileSignature> {
  @service declare features: FeaturesService;

  get eligibilityFilesBaseUrl() {
    const opts: QueryUrlOptions = {
      identifier: { type: 'eligibility-files' },
      op: 'query',
      resourcePath: 'eligibility-files',
    };
    return buildBaseURL(opts);
  }

  // Text downloads use direct fetch, so they need explicit auth headers.
  get tioAuthHeaders() {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
      contentType: 'application/vnd.api+json',
    });
  }

  downloadEligibilityFile = dropTask(async () => {
    const response = await fetch(
      `${this.eligibilityFilesBaseUrl}/get-download-url/${this.args.processId}/${this.args.companyId}/${this.args.legacyId}`,
      {
        method: 'GET',
        headers: this.tioAuthHeaders,
      }
    );
    if (response.ok) {
      const fileUrl = await response.text();
      const link = document.createElement('a');
      link.href = fileUrl;
      link.setAttribute('download', this.args.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      console.error('Unable to download file due to an error.', response);
    }
  });

  <template>
    {{#if (eq @format "text-button")}}
      <Button
        @intent="primary"
        @label={{t "eligibility_files.download"}}
        {{on "click" this.downloadEligibilityFile.perform}}
      />
    {{else}}
      <Button
        @intent="primary"
        @appearance="outlined"
        title={{@title}}
        {{on "click" this.downloadEligibilityFile.perform}}
        @icon={{PhTrayArrowDown}}
      />
    {{/if}}
  </template>
}
